import React, { useEffect, useMemo, useState } from 'react';
import { fromUnixTime } from 'date-fns';

import useMeteorologyStatus from 'hooks/NewHome/Table/MeteorologyStatus/useMeteorologyStatus';
import useFluxStatus from 'hooks/NewHome/Table/FluxStatus/useFluxStatus';
import useFluxQueue from 'hooks/NewHome/Table/FluxQueue/useFluxQueue';
import useListHomeWarnings from 'hooks/Home/useListHomeWarnings';
import Icon from 'components/DesignSystem/Icon';
import Warnings from 'components/NewHome/Table/Warnings';
import MobileStatusList from 'components/NewHome/Mobile/MobileStatusList';
import FilterSheet, { FilterField } from 'components/NewHome/Mobile/FilterSheet';
import { StatusListItem } from 'components/NewHome/Mobile/StatusAccordionItem';
import { cssVar } from 'constants/theme';

import './index.scss';

export type StatusTab = 'meteo' | 'flux' | 'queue' | 'warnings';

const TABS: { key: StatusTab; label: string }[] = [
  { key: 'meteo', label: 'Status Meteo' },
  { key: 'flux', label: 'Status Flux' },
  { key: 'queue', label: 'Fila Flux' },
  { key: 'warnings', label: 'Avisos' },
];

export default function MobileStatusCard({
  initialTab = 'meteo',
  activationKey,
}: { initialTab?: StatusTab; activationKey?: string }) {
  const [activeTab, setActiveTab] = useState<StatusTab>(initialTab);
  const [search, setSearch] = useState<string>('');
  const [filterOpen, setFilterOpen] = useState<boolean>(false);

  const meteo = useMeteorologyStatus();
  const flux = useFluxStatus();
  const queue = useFluxQueue();
  const warningsData = useListHomeWarnings();

  useEffect(() => {
    setActiveTab(initialTab);
    // activationKey muda a cada clique no sino (mesmo com a mesma URL), forçando
    // o retorno à aba de avisos mesmo se o usuário já tiver trocado de aba manualmente.
  }, [initialTab, activationKey]);

  const meteoItems: StatusListItem[] = useMemo(() => meteo.filteredItems.map((item) => ({
    key: item.id,
    primary: (
      <>
        <span className="status-model">{item.modelName}</span>
        <span className="status-sep">{' · '}</span>
        <span className="status-variable">{item.variable}</span>
      </>
    ),
    secondary: `${item.forecastDate} · ${item.runtime} · ${item.member}`,
    badge: { text: item.status, color: item.statusColor },
    details: [
      { label: 'Data previsão', value: item.forecastDate },
      { label: 'Runtime', value: item.runtime },
      { label: 'Membro', value: item.member },
      { label: 'Última atualização', value: item.lastUpdate },
    ],
    searchText: `${item.modelName} ${item.variable} ${item.forecastDate} ${item.runtime} ${item.member} ${item.status}`.toLowerCase(),
  })), [meteo.filteredItems]);

  const fluxItems: StatusListItem[] = useMemo(() => flux.filteredItems.map((item) => ({
    key: item.id,
    primary: item.cenario,
    secondary: `${item.data_acomph} · ${item.data_previsao}`,
    details: [
      { label: 'Data Acomph', value: item.data_acomph },
      { label: 'Data Previsão', value: item.data_previsao },
      { label: 'Última atualização', value: fromUnixTime(item.last_update).toLocaleString() },
    ],
    searchText: `${item.cenario} ${item.data_acomph} ${item.data_previsao}`.toLowerCase(),
  })), [flux.filteredItems]);

  const queueItems: StatusListItem[] = useMemo(() => queue.filteredItems.map((item) => ({
    key: item.id,
    primary: item.title,
    badge: { text: item.execution },
    details: [
      { label: 'Título', value: item.title },
      { label: 'Em execução', value: item.execution },
    ],
    searchText: `${item.title} ${item.execution}`.toLowerCase(),
  })), [queue.filteredItems]);

  const activeItems = useMemo(() => {
    const source = { meteo: meteoItems, flux: fluxItems, queue: queueItems }[activeTab as 'meteo' | 'flux' | 'queue'] || [];
    const query = search.trim().toLowerCase();
    if (!query) {
      return source;
    }
    return source.filter((item) => (item.searchText || '').includes(query));
  }, [activeTab, meteoItems, fluxItems, queueItems, search]);

  const fields: FilterField[] = useMemo(() => {
    if (activeTab === 'meteo') {
      return [
        {
          label: 'Modelo', value: meteo.model, setValue: meteo.setModel, options: meteo.modelOptions,
        },
        {
          label: 'Data de previsão', value: meteo.forecastDate, setValue: meteo.setForecastDate, options: meteo.forecastDateOptions,
        },
        {
          label: 'Runtime', value: meteo.runtime, setValue: meteo.setRuntime, options: meteo.runtimeOptions,
        },
        {
          label: 'Membro', value: meteo.member, setValue: meteo.setMember, options: meteo.memberOptions,
        },
        {
          label: 'Variável', value: meteo.variable, setValue: meteo.setVariable, options: meteo.variableOptions,
        },
        {
          label: 'Status', value: meteo.status, setValue: meteo.setStatus, options: meteo.statusOptions,
        },
      ];
    }
    if (activeTab === 'flux') {
      return [
        {
          label: 'Cenário', value: flux.scenarios, setValue: flux.setScenarios, options: flux.scenarioOptions,
        },
        {
          label: 'Data Acomph', value: flux.acomphDates, setValue: flux.setAcomphDates, options: flux.acomphDateOptions,
        },
        {
          label: 'Data Previsão', value: flux.forecastDates, setValue: flux.setForecastDates, options: flux.forecastDateOptions,
        },
      ];
    }
    if (activeTab === 'queue') {
      return [
        {
          label: 'Título', value: queue.title, setValue: queue.setTitle, options: queue.titleOptions,
        },
        {
          label: 'Em execução', value: queue.execution, setValue: queue.setExecution, options: queue.executionOptions,
        },
      ];
    }
    return [];
  }, [activeTab, meteo, flux, queue]);

  const onClearFilters = () => {
    if (activeTab === 'meteo') {
      meteo.clearFilters();
    } else if (activeTab === 'flux') {
      flux.clearFilters();
    } else if (activeTab === 'queue') {
      queue.clearFilters();
    }
  };

  const activeFilterCount = fields.reduce((total, field) => total + field.value.length, 0);

  return (
    <div className="mobile-status-card">
      <div className="mobile-status-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`mobile-status-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab !== 'warnings' && (
        <div className="mobile-status-toolbar">
          <div className="mobile-status-search">
            <Icon iconType="search" color={cssVar('text-muted')} size="small" />
            <input
              type="search"
              aria-label="Buscar modelo, variável"
              placeholder="Buscar modelo, variável…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <button
            type="button"
            className="mobile-status-filters-button"
            onClick={() => setFilterOpen(true)}
          >
            <Icon iconType="filter" color={cssVar('text-on-accent')} size="small" />
            Filtros
            {activeFilterCount > 0 && (
              <span className="mobile-status-filters-count">{activeFilterCount}</span>
            )}
          </button>
        </div>
      )}

      {activeTab === 'warnings' ? (
        <Warnings
          warnings={warningsData.warnings}
          visible
          refetch={warningsData.refetch}
          updateLastSeen={warningsData.updateLastSeen}
        />
      ) : (
        <MobileStatusList key={activeTab} items={activeItems} />
      )}

      <FilterSheet
        open={filterOpen}
        dismiss={() => setFilterOpen(false)}
        fields={fields}
        onClear={onClearFilters}
      />
    </div>
  );
}
